import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { togglePillClass } from '@course/components/toggle-pill';
import { addDays, mondayOf } from 'shared/checkin-weeks';
import {
  loggedStepsMessage,
  MAX_FUNNELS,
  MAX_FUNNEL_STAGES,
  MAX_LABEL_LENGTH,
  MAX_SHORT_LENGTH,
  TrackerChannel,
  TrackerFunnel,
  TrackerStage,
} from 'shared/tracker-config';
import { fmtMoney, fmtTrackerDate, moneyInputValue, parseMoneyCents } from 'shared/tracker-people';
import {
  ALL_CHANNELS_LABEL,
  cplByChannel,
  cplTrend,
  DESTINATION_FALLBACK,
  fmtHours,
  funnelFromFamily,
  funnelKeys,
  keyDraftStages,
  draftStageKey,
  leadsSetUpProblem,
  spendEntryProblem,
  MAP_FIRST_CHANNEL_MESSAGE,
  MAX_DESTINATION_LENGTH,
  MAX_SPEND_NOTE_LENGTH,
  parseHoursMinutes,
  TrackerSpendEntry,
} from 'shared/tracker-leads';
import { spendEntryCents } from 'shared/tracker-rollup';
import { trackerSpend } from 'shared/tracker-summary';
import { fmtWeekRange } from 'shared/tracker-coach';
import { FieldError, Panel, QuickDates } from './panel';
import {
  CARD_CLASS,
  HELP_CLASS,
  INPUT_CLASS,
  LABEL_CLASS,
  LINK_CLASS,
  Person,
  readError,
  TrackerData,
} from './helpers';

type Participant = TrackerData['participant'];

/** What a set-up or channel save sends back for the page to swap in. */
export type LeadsSaved = {
  participant: Participant;
  config: TrackerData['config'];
  people?: Person[];
  spend?: TrackerSpendEntry[];
};

/**
 * "Set up your tracker" (week one): the participant's destination, hourly
 * rate and cost-per-lead target. The scoreboard's Change opens it again.
 */
export function LeadsSetUpForm({
  courseId,
  participant,
  hasHours,
  isChange,
  onSaved,
  onCancel,
}: {
  courseId: UUID;
  participant: Participant;
  /** Hours are logged: a new rate reprices them, which the form says. */
  hasHours: boolean;
  isChange: boolean;
  onSaved: (saved: LeadsSaved) => void;
  onCancel?: () => void;
}) {
  const [destination, setDestination] = useState(participant.destination ?? '');
  const [rate, setRate] = useState(moneyInputValue(participant.hourlyRateCents));
  const [target, setTarget] = useState(moneyInputValue(participant.cplTargetCents));
  const [error, setError] = useState<{ field: string; message: string } | undefined>();
  const [saving, setSaving] = useState(false);
  const rateCents = parseMoneyCents(rate);
  const title = isChange ? 'Destination, rate and target' : 'Set up your tracker';

  async function save(e: Event) {
    e.preventDefault();
    const targetCents = parseMoneyCents(target);
    const problem = leadsSetUpProblem({
      destination,
      hourlyRateCents: rateCents,
      cplTargetCents: targetCents,
    });
    if (problem || rateCents === undefined || targetCents === undefined) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      onSaved(
        await rpx.tracker.setLeadsSetUp({
          courseId,
          destination,
          hourlyRateCents: rateCents,
          cplTargetCents: targetCents,
        }),
      );
      showToast({ type: 'ok', title: 'Saved your destination, rate and target', message: '' });
    } catch (err) {
      const { message, field } = readError(err);
      setError({ field: field ?? 'form', message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class={CARD_CLASS} onSubmit={save} aria-label={title}>
      <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <label class="flex flex-col gap-1">
        <span class={LABEL_CLASS}>Your destination</span>
        <input
          class={`${INPUT_CLASS} max-w-80`}
          placeholder="e.g. Booked call"
          maxLength={MAX_DESTINATION_LENGTH}
          value={destination}
          aria-describedby="leads-destination-help"
          onInput={(e) => setDestination(e.currentTarget.value)}
        />
        <span id="leads-destination-help" class={HELP_CLASS}>
          The one next step you move every lead toward.
        </span>
        {error?.field === 'destination' && (
          <FieldError id="leads-destination-error" message={error.message} />
        )}
      </label>
      <label class="flex flex-col gap-1">
        <span class={LABEL_CLASS}>Your hourly rate</span>
        <span class="flex items-center gap-2">
          <span class="text-gray-500 dark:text-gray-400">$</span>
          <input
            class={`${INPUT_CLASS} max-w-32`}
            inputMode="decimal"
            value={rate}
            aria-describedby="leads-rate-help"
            onInput={(e) => setRate(e.currentTarget.value)}
          />
        </span>
        <span id="leads-rate-help" class={HELP_CLASS}>
          Use the formula from week one. Hours you log are priced at this rate.
        </span>
        {isChange &&
          hasHours &&
          rateCents !== undefined &&
          rateCents !== participant.hourlyRateCents && (
            <span class="text-sm text-amber-800 dark:text-amber-300">
              Your logged hours are repriced at {fmtMoney(rateCents)}, so past weeks’ cost per lead
              will change.
            </span>
          )}
        {error?.field === 'rate' && <FieldError id="leads-rate-error" message={error.message} />}
      </label>
      <label class="flex flex-col gap-1">
        <span class={LABEL_CLASS}>Your cost-per-lead target</span>
        <span class="flex items-center gap-2">
          <span class="text-gray-500 dark:text-gray-400">$</span>
          <input
            class={`${INPUT_CLASS} max-w-32`}
            inputMode="decimal"
            value={target}
            onInput={(e) => setTarget(e.currentTarget.value)}
          />
        </span>
        {error?.field === 'target' && (
          <FieldError id="leads-target-error" message={error.message} />
        )}
      </label>
      {error && !['destination', 'rate', 'target'].includes(error.field) && (
        <FieldError id="leads-setup-error" message={error.message} />
      )}
      <div class="flex gap-2">
        <BtnPrimary type="submit" isLoading={saving}>
          Save
        </BtnPrimary>
        {onCancel && (
          <BtnSecondary type="button" onClick={onCancel}>
            Cancel
          </BtnSecondary>
        )}
      </div>
    </form>
  );
}

let draftSeq = 0;

/** Each stage's logged steps and people, from the participant's own data. */
function stageUsage(data: TrackerData) {
  const steps = new Map<string, number>();
  for (const t of data.touches) {
    if (t.kind === 'step' && t.stageKey) {
      steps.set(t.stageKey, (steps.get(t.stageKey) ?? 0) + 1);
    }
  }
  const people = new Map<string, number>();
  for (const p of data.people) {
    if (p.stageKey) {
      people.set(p.stageKey, (people.get(p.stageKey) ?? 0) + 1);
    }
  }
  return { steps, people };
}

function peopleIn(data: TrackerData, channelKey: string) {
  return data.people.filter((p) => p.channelKey === channelKey).length;
}

const ARROW_CLASS =
  'min-h-11 min-w-11 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30';

/**
 * One of the participant's channels, being added or changed: its name, its
 * stages in order (name, short name, move, remove) and the destination as
 * the fixed last row. Save channel writes it; a stage with logged steps
 * can't be removed.
 */
function FunnelForm({
  courseId,
  data,
  initial,
  isNew,
  onSaved,
  onCancel,
}: {
  courseId: UUID;
  data: TrackerData;
  initial: TrackerFunnel;
  isNew: boolean;
  onSaved: (saved: LeadsSaved) => void;
  onCancel: () => void;
}) {
  const [funnel, setFunnel] = useState(initial);
  const [error, setError] = useState<string | undefined>();
  const [blocked, setBlocked] = useState<Record<string, string>>({});
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [saving, setSaving] = useState(false);
  const destination = data.participant.destination || DESTINATION_FALLBACK;
  const family = data.families.find((f) => f.key === funnel.familyKey);
  const usage = stageUsage(data);
  const inChannel = isNew ? 0 : peopleIn(data, funnel.key);
  const taggedSpend = data.spend.filter((s) => s.channelKey === funnel.key).length;
  const name = funnel.label.trim() || 'this channel';

  function patchStage(i: number, change: Partial<TrackerStage>) {
    setFunnel((f) => ({
      ...f,
      stages: f.stages.map((s, j) => (j === i ? { ...s, ...change } : s)),
    }));
    setError(undefined);
  }

  function move(i: number, offset: number) {
    setFunnel((f) => {
      const j = i + offset;
      if (j < 0 || j >= f.stages.length) {
        return f;
      }
      const stages = [...f.stages];
      [stages[i], stages[j]] = [stages[j], stages[i]];
      return { ...f, stages };
    });
  }

  function removeStage(i: number) {
    const stage = funnel.stages[i];
    const steps = usage.steps.get(stage.key) ?? 0;
    const people = usage.people.get(stage.key) ?? 0;
    const label = stage.label.trim() || stage.key;
    if (steps || people) {
      setBlocked({
        ...blocked,
        [stage.key]: steps
          ? loggedStepsMessage(steps, label)
          : `${people === 1 ? '1 person is' : `${people} people are`} on “${label}”. Rename it instead.`,
      });
      return;
    }
    setFunnel((f) => ({ ...f, stages: f.stages.filter((_, j) => j !== i) }));
  }

  /** New stages get keys from their names at save, as course settings do. */
  function withKeys(): TrackerFunnel {
    return keyDraftStages(funnel, [
      ...funnelKeys(data.participant.funnels),
      ...data.config.sequence.map((s) => s.key),
    ]);
  }

  async function save(e: Event) {
    e.preventDefault();
    if (!funnel.label.trim()) {
      setError('Name this channel, like “LinkedIn”.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      onSaved(await rpx.tracker.saveFunnel({ courseId, funnel: withKeys() }));
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    try {
      onSaved(await rpx.tracker.removeFunnel({ courseId, funnelKey: funnel.key }));
      showToast({ type: 'ok', title: `Removed ${name}`, message: '' });
    } catch (err) {
      setError(readError(err).message);
      setConfirmRemove(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="flex flex-col gap-3" onSubmit={save} aria-label={`Channel ${name}`}>
      <label class="flex flex-col gap-1">
        <span class={LABEL_CLASS}>Channel name</span>
        <input
          class={`${INPUT_CLASS} max-w-80`}
          maxLength={MAX_LABEL_LENGTH}
          value={funnel.label}
          placeholder="e.g. LinkedIn"
          onInput={(e) => {
            const label = e.currentTarget.value;
            setFunnel((f) => ({ ...f, label }));
          }}
        />
        {family && <span class={HELP_CLASS}>From the {family.label} family</span>}
      </label>
      <fieldset class="flex flex-col gap-2">
        <legend class={`${LABEL_CLASS} mb-1`}>Stages, in order</legend>
        <ol class="flex flex-col gap-2">
          {funnel.stages.map((stage, i) => (
            <li key={stage.key} class="flex flex-col gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="flex items-center gap-2 grow basis-56 min-w-0">
                  <span class="w-5 shrink-0 text-right text-sm text-gray-500 dark:text-gray-400">
                    {i + 1}.
                  </span>
                  <input
                    class={INPUT_CLASS}
                    maxLength={MAX_LABEL_LENGTH}
                    value={stage.label}
                    placeholder="Stage name"
                    aria-label={`Stage ${i + 1} name`}
                    onInput={(e) => patchStage(i, { label: e.currentTarget.value })}
                  />
                </span>
                <span class="flex items-center gap-1 pl-7">
                  <input
                    class={`${INPUT_CLASS} w-32`}
                    maxLength={MAX_SHORT_LENGTH}
                    value={stage.short ?? ''}
                    placeholder="Short name"
                    aria-label={`Stage ${i + 1} short name, for the weekly numbers`}
                    onInput={(e) => patchStage(i, { short: e.currentTarget.value })}
                  />
                  <Button
                    class={ARROW_CLASS}
                    aria-label={`Move stage ${i + 1} up`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    class={ARROW_CLASS}
                    aria-label={`Move stage ${i + 1} down`}
                    disabled={i === funnel.stages.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    class={`${LINK_CLASS} min-h-11 px-1 disabled:opacity-40`}
                    aria-label={`Remove stage ${i + 1}`}
                    disabled={funnel.stages.length <= 1}
                    onClick={() => removeStage(i)}
                  >
                    Remove
                  </Button>
                </span>
              </div>
              {blocked[stage.key] && (
                <p class="pl-7 text-sm text-amber-800 dark:text-amber-300">{blocked[stage.key]}</p>
              )}
            </li>
          ))}
          <li class="flex items-center gap-2">
            <span class="w-5 shrink-0 text-right text-sm text-gray-500 dark:text-gray-400">
              {funnel.stages.length + 1}.
            </span>
            <span class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm grow min-w-0">
              <span class="font-medium text-gray-900 dark:text-gray-100">{destination}</span>
              <span class={HELP_CLASS}>Your destination, always last</span>
            </span>
          </li>
        </ol>
        {funnel.stages.length < MAX_FUNNEL_STAGES && (
          <Button
            class={`${LINK_CLASS} self-start`}
            onClick={() =>
              setFunnel((f) => ({
                ...f,
                stages: [...f.stages, { key: draftStageKey((draftSeq += 1)), label: '' }],
              }))
            }
          >
            Add stage
          </Button>
        )}
      </fieldset>
      <FieldError id="funnel-error" message={error} />
      {confirmRemove && (
        <div
          role="alert"
          class="flex flex-col gap-2 rounded-md border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm"
        >
          {inChannel ? (
            <>
              <span>
                {inChannel === 1 ? '1 person is' : `${inChannel} people are`} in “{name}”. Rename
                the channel instead.
              </span>
              <BtnSecondary
                type="button"
                class="self-start"
                onClick={() => setConfirmRemove(false)}
              >
                OK
              </BtnSecondary>
            </>
          ) : (
            <>
              <span>
                Remove “{name}”?
                {taggedSpend > 0 &&
                  ` Its ${taggedSpend === 1 ? 'spend entry moves' : `${taggedSpend} spend entries move`} to All channels.`}
              </span>
              <span class="flex gap-2">
                <BtnPrimary type="button" isLoading={saving} onClick={remove}>
                  Remove channel
                </BtnPrimary>
                <BtnSecondary type="button" onClick={() => setConfirmRemove(false)}>
                  Keep channel
                </BtnSecondary>
              </span>
            </>
          )}
        </div>
      )}
      <div class="flex flex-wrap items-center gap-2">
        <BtnPrimary type="submit" isLoading={saving && !confirmRemove}>
          Save channel
        </BtnPrimary>
        <BtnSecondary type="button" onClick={onCancel}>
          Cancel
        </BtnSecondary>
        {!isNew && !confirmRemove && (
          <Button class={`${LINK_CLASS} ml-auto`} onClick={() => setConfirmRemove(true)}>
            Remove channel
          </Button>
        )}
      </div>
    </form>
  );
}

/** "Pick a family": the course's channel families, each opening a new channel. */
function FamilyPicker({
  families,
  onPick,
  onCancel,
}: {
  families: TrackerChannel[];
  onPick: (family: TrackerChannel) => void;
  onCancel?: () => void;
}) {
  return (
    <div class="flex flex-col gap-2">
      <span class={LABEL_CLASS}>Pick a family</span>
      <ul class="grid gap-2 sm:grid-cols-2">
        {families.map((family) => (
          <li key={family.key}>
            <button
              type="button"
              class="w-full min-h-11 text-left rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950"
              onClick={() => onPick(family)}
            >
              <span class="block text-sm font-medium text-gray-900 dark:text-gray-100">
                {family.label}
              </span>
              <span class="block text-xs text-gray-600 dark:text-gray-400">
                {family.stages.map((s) => s.label).join(' → ')}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {onCancel && (
        <BtnSecondary type="button" class="self-start" onClick={onCancel}>
          Cancel
        </BtnSecondary>
      )}
    </div>
  );
}

/** "LinkedIn: Request sent → Accepted → Booked call". */
export function channelPath(channel: TrackerChannel) {
  return channel.stages.map((s) => s.label).join(' → ');
}

type Editing = { kind: 'pick' } | { kind: 'funnel'; funnel: TrackerFunnel; isNew: boolean };

/**
 * "Your channels" (week two): the participant's channels, each from a
 * family, with Add channel and a channel's Change. With none, it opens on
 * the family picker.
 */
export function ChannelsEditor({
  courseId,
  data,
  onSaved,
  onDone,
}: {
  courseId: UUID;
  data: TrackerData;
  onSaved: (saved: LeadsSaved) => void;
  /** In a panel: after a channel saves (true) or the picker is cancelled (false). */
  onDone?: (saved: boolean) => void;
}) {
  const funnels = data.participant.funnels;
  const [editing, setEditing] = useState<Editing | undefined>(
    funnels.length ? undefined : { kind: 'pick' },
  );

  function pick(family: TrackerChannel) {
    const reserved = data.config.sequence.map((s) => s.key);
    setEditing({
      kind: 'funnel',
      funnel: funnelFromFamily(family, funnels, reserved),
      isNew: true,
    });
  }

  if (editing?.kind === 'pick') {
    return (
      <FamilyPicker
        families={data.families}
        onPick={pick}
        onCancel={
          funnels.length ? () => setEditing(undefined) : onDone ? () => onDone(false) : undefined
        }
      />
    );
  }
  if (editing?.kind === 'funnel') {
    return (
      <FunnelForm
        key={editing.funnel.key}
        courseId={courseId}
        data={data}
        initial={editing.funnel}
        isNew={editing.isNew}
        onSaved={(saved) => {
          onSaved(saved);
          setEditing(undefined);
          onDone?.(true);
        }}
        onCancel={() => setEditing(funnels.length ? undefined : { kind: 'pick' })}
      />
    );
  }
  return (
    <div class="flex flex-col gap-2">
      <ul class="flex flex-col gap-2">
        {data.config.channels.map((channel) => {
          const funnel = funnels.find((f) => f.key === channel.key);
          return (
            <li key={channel.key} class="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span class="font-medium text-gray-900 dark:text-gray-100">{channel.label}</span>
              <span class="text-gray-600 dark:text-gray-400 min-w-0">{channelPath(channel)}</span>
              {funnel && (
                <Button
                  class={LINK_CLASS}
                  aria-label={`Change ${channel.label}`}
                  onClick={() => setEditing({ kind: 'funnel', funnel, isNew: false })}
                >
                  Change
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {funnels.length < MAX_FUNNELS && (
        <Button class={`${LINK_CLASS} self-start`} onClick={() => setEditing({ kind: 'pick' })}>
          Add channel
        </Button>
      )}
    </div>
  );
}

/** The "Your channels" card: the empty state, then the participant's channels. */
export function ChannelsCard({
  courseId,
  data,
  onSaved,
}: {
  courseId: UUID;
  data: TrackerData;
  onSaved: (saved: LeadsSaved) => void;
}) {
  return (
    <section class={CARD_CLASS} aria-label="Your channels">
      <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">Your channels</h2>
      {!data.participant.funnels.length && (
        <p class="text-sm text-gray-700 dark:text-gray-300">{MAP_FIRST_CHANNEL_MESSAGE}</p>
      )}
      <ChannelsEditor courseId={courseId} data={data} onSaved={onSaved} />
    </section>
  );
}

/** "$40" or "1.5 h", the way an entry was logged. */
export function spendAmountText(entry: TrackerSpendEntry) {
  return entry.dollarsCents !== undefined
    ? fmtMoney(entry.dollarsCents)
    : fmtHours(entry.minutes ?? 0);
}

function channelName(data: TrackerData, key?: string) {
  return data.config.channels.find((c) => c.key === key)?.label ?? ALL_CHANNELS_LABEL;
}

type Unit = 'dollars' | 'hours';

/**
 * Add spend: date, channel, Dollars | Hours, amount and note; then this
 * week's entries, each with Edit and Delete.
 */
export function SpendPanel({
  courseId,
  data,
  onSpend,
  close,
}: {
  courseId: UUID;
  data: TrackerData;
  onSpend: (spend: TrackerSpendEntry[]) => void;
  close: () => void;
}) {
  const { today } = data;
  const [editingId, setEditingId] = useState<string | undefined>();
  const [spentOn, setSpentOn] = useState(today);
  const [channelKey, setChannelKey] = useState('');
  const [unit, setUnit] = useState<Unit>('dollars');
  /** The add form's channel and unit while an entry is being edited, to return to after. */
  const [addChoice, setAddChoice] = useState<{ channelKey: string; unit: Unit } | undefined>();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const weekStart = mondayOf(today);
  const thisWeek = [...data.spend]
    .filter((s) => s.spentOn >= weekStart && s.spentOn <= addDays(weekStart, 6))
    .reverse();

  /**
   * Clears the form for the next entry. After adding, the channel and unit
   * stay, for a run of entries; leaving Edit brings back the add form's own.
   */
  function reset() {
    if (editingId && addChoice) {
      setChannelKey(addChoice.channelKey);
      setUnit(addChoice.unit);
    }
    setAddChoice(undefined);
    setEditingId(undefined);
    setSpentOn(today);
    setAmount('');
    setNote('');
    setError(undefined);
  }

  function edit(entry: TrackerSpendEntry) {
    if (!editingId) {
      setAddChoice({ channelKey, unit });
    }
    setEditingId(entry.id);
    setSpentOn(entry.spentOn);
    setChannelKey(entry.channelKey ?? '');
    setUnit(entry.dollarsCents !== undefined ? 'dollars' : 'hours');
    setAmount(
      entry.dollarsCents !== undefined
        ? moneyInputValue(entry.dollarsCents)
        : String(Math.round(((entry.minutes ?? 0) / 60) * 100) / 100),
    );
    setNote(entry.note);
    setError(undefined);
  }

  async function save(e: Event) {
    e.preventDefault();
    const dollarsCents = unit === 'dollars' ? parseMoneyCents(amount) : undefined;
    const minutes = unit === 'hours' ? parseHoursMinutes(amount) : undefined;
    if (unit === 'dollars' && !dollarsCents) {
      setError('Enter the dollars you spent, like 40.');
      return;
    }
    if (unit === 'hours' && !minutes) {
      setError('Enter the hours you spent, like 1.5.');
      return;
    }
    const problem = spendEntryProblem({ dollarsCents, minutes });
    if (problem) {
      setError(problem);
      return;
    }
    const entry = {
      spentOn,
      channelKey: channelKey || undefined,
      dollarsCents,
      minutes,
      note,
    };
    setSaving(true);
    setError(undefined);
    try {
      const spend = editingId
        ? await rpx.tracker.updateSpend({ courseId, spendId: editingId as UUID, entry })
        : await rpx.tracker.addSpend({ courseId, entry });
      onSpend(spend);
      reset();
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(entry: TrackerSpendEntry) {
    try {
      onSpend(await rpx.tracker.deleteSpend({ courseId, spendId: entry.id as UUID }));
      if (editingId === entry.id) {
        reset();
      }
      showToast({
        type: 'ok',
        title: `Deleted ${spendAmountText(entry)} spend`,
        message: '',
        timeout: 8000,
        action: {
          label: 'Undo',
          onClick: () => {
            rpx.tracker
              .addSpend({
                courseId,
                entry: {
                  spentOn: entry.spentOn,
                  channelKey: entry.channelKey,
                  dollarsCents: entry.dollarsCents,
                  minutes: entry.minutes,
                  note: entry.note,
                },
              })
              .then(onSpend)
              .catch((err) =>
                showToast({
                  type: 'warn',
                  title: 'Couldn’t undo',
                  message: readError(err).message,
                }),
              );
          },
        },
      });
    } catch (err) {
      setError(readError(err).message);
    }
  }

  return (
    <Panel title={editingId ? 'Change spend' : 'Add spend'} close={close}>
      <form class="flex flex-col gap-4" onSubmit={save}>
        <div class="flex flex-col gap-1">
          <label class={LABEL_CLASS} for="spend-date">
            Date
          </label>
          <div class="flex items-center gap-3 flex-wrap">
            <input
              id="spend-date"
              type="date"
              class={`${INPUT_CLASS} max-w-44`}
              value={spentOn}
              max={today}
              onChange={(e) => setSpentOn(e.currentTarget.value || today)}
            />
            <QuickDates today={today} yesterday={addDays(today, -1)} onPick={setSpentOn} />
          </div>
        </div>
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Channel</span>
          <select
            class={INPUT_CLASS}
            value={channelKey}
            onChange={(e) => setChannelKey(e.currentTarget.value)}
          >
            <option value="">{ALL_CHANNELS_LABEL}</option>
            {data.config.channels.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <div class="flex flex-col gap-2">
          <div class="flex gap-2" role="group" aria-label="Spent in">
            {(['dollars', 'hours'] as const).map((u) => (
              <button
                key={u}
                type="button"
                aria-pressed={unit === u}
                class={togglePillClass(unit === u)}
                onClick={() => setUnit(u)}
              >
                {u === 'dollars' ? 'Dollars' : 'Hours'}
              </button>
            ))}
          </div>
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>Amount</span>
            <span class="flex items-center gap-2">
              {unit === 'dollars' && <span class="text-gray-500 dark:text-gray-400">$</span>}
              <input
                class={`${INPUT_CLASS} max-w-32`}
                inputMode="decimal"
                value={amount}
                onInput={(e) => setAmount(e.currentTarget.value)}
              />
              {unit === 'hours' && <span class="text-gray-500 dark:text-gray-400">h</span>}
            </span>
            {unit === 'hours' && (
              <span class={HELP_CLASS}>
                Priced at your rate, {fmtMoney(data.participant.hourlyRateCents ?? 0)} an hour.
              </span>
            )}
          </label>
        </div>
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Note</span>
          <input
            class={INPUT_CLASS}
            maxLength={MAX_SPEND_NOTE_LENGTH}
            value={note}
            placeholder="Optional"
            onInput={(e) => setNote(e.currentTarget.value)}
          />
        </label>
        <FieldError id="spend-error" message={error} />
        <div class="flex gap-2">
          <BtnPrimary type="submit" isLoading={saving}>
            {editingId ? 'Save changes' : 'Add spend'}
          </BtnPrimary>
          {editingId && (
            <BtnSecondary type="button" onClick={reset}>
              Cancel
            </BtnSecondary>
          )}
        </div>
      </form>
      <section class="flex flex-col gap-2" aria-label="This week’s spend">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          This week ({thisWeek.length})
        </h3>
        {!thisWeek.length && (
          <p class="text-sm text-gray-600 dark:text-gray-400">No spend logged this week.</p>
        )}
        <ul class="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {thisWeek.map((entry) => (
            <li key={entry.id} class="flex items-center gap-2 py-2 text-sm">
              <span class="grow min-w-0">
                <span class="block text-gray-900 dark:text-gray-100 tabular-nums">
                  {spendAmountText(entry)} · {channelName(data, entry.channelKey)}
                </span>
                <span class="block text-xs text-gray-600 dark:text-gray-400 truncate">
                  {[fmtTrackerDate(entry.spentOn, today), entry.note].filter(Boolean).join(' · ')}
                </span>
              </span>
              <Button class={`${LINK_CLASS} min-h-11 px-1`} onClick={() => edit(entry)}>
                Edit
              </Button>
              <Button class={`${LINK_CLASS} min-h-11 px-1`} onClick={() => remove(entry)}>
                Delete
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </Panel>
  );
}

const TABLE_WRAP = 'overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md';
const TH = 'px-3 py-2 font-medium';
const TD = 'px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100';

function cents(value?: number) {
  return value === undefined ? '–' : fmtMoney(value);
}

/**
 * Trend: a row per week from the first spend or step: spend, leads, cost
 * per lead that week and to date, under or over the participant's target.
 */
export function TrendTable({ data }: { data: TrackerData }) {
  const spend = trackerSpend(data)!;
  const rows = cplTrend({
    config: data.config,
    touches: data.touches,
    spend,
    targetCents: data.participant.cplTargetCents,
    today: data.today,
  });
  if (!rows.length) {
    return;
  }
  return (
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Trend</h3>
      <div class={TABLE_WRAP}>
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-400">
            <tr>
              <th class={`${TH} text-left`}>Week</th>
              <th class={`${TH} text-right`}>Spend</th>
              <th class={`${TH} text-right`}>Leads</th>
              <th class={`${TH} text-right`}>Cost per lead</th>
              <th class={`${TH} text-right`}>To date</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((row) => (
              <tr key={row.weekStart}>
                <th
                  scope="row"
                  class="px-3 py-2 text-left font-medium normal-case text-gray-900 dark:text-gray-100"
                >
                  <span class="block whitespace-nowrap">{fmtWeekRange(row.weekStart)}</span>
                  {row.isCurrent && (
                    <span class="block text-xs font-normal text-gray-500 dark:text-gray-400">
                      so far
                    </span>
                  )}
                </th>
                <td class={TD}>{fmtMoney(row.spendCents)}</td>
                <td class={TD}>{row.leads}</td>
                <td class={TD}>{cents(row.cplCents)}</td>
                <td class={TD}>
                  {cents(row.toDateCplCents)}
                  {row.vsTarget && (
                    <span
                      class={`block text-xs ${
                        row.vsTarget === 'under'
                          ? 'text-green-700 dark:text-green-400'
                          : 'text-amber-700 dark:text-amber-400'
                      }`}
                    >
                      {row.vsTarget}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** By channel: leads, tagged spend and cost per lead; untagged spend is its own row. */
export function ByChannelTable({ data }: { data: TrackerData }) {
  const { rows, total } = cplByChannel({
    config: data.config,
    touches: data.touches,
    spend: trackerSpend(data)!,
  });
  if (!rows.length) {
    return;
  }
  return (
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">By channel</h3>
      <div class={TABLE_WRAP}>
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-400">
            <tr>
              <th class={`${TH} text-left`}>Channel</th>
              <th class={`${TH} text-right`}>Leads</th>
              <th class={`${TH} text-right`}>Spend</th>
              <th class={`${TH} text-right`}>Cost per lead</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            {[...rows, total].map((row) => (
              <tr key={row.key ?? row.label} class={row === total ? 'font-semibold' : ''}>
                <th
                  scope="row"
                  class="px-3 py-2 text-left font-medium normal-case text-gray-900 dark:text-gray-100"
                >
                  {row.label}
                </th>
                <td class={TD}>{row.leads ?? '–'}</td>
                <td class={TD}>{fmtMoney(row.spendCents)}</td>
                <td class={TD}>{cents(row.cplCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** All spend: every entry, newest first, then the total per channel. */
export function AllSpendList({ data }: { data: TrackerData }) {
  if (!data.spend.length) {
    return;
  }
  const rate = data.participant.hourlyRateCents;
  const totals = new Map<string, number>();
  for (const entry of data.spend) {
    const name = channelName(data, entry.channelKey);
    totals.set(name, (totals.get(name) ?? 0) + spendEntryCents(entry, rate));
  }
  return (
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">All spend</h3>
      <ul class="flex flex-col divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
        {[...data.spend].reverse().map((entry) => (
          <li key={entry.id} class="flex items-baseline gap-3 px-3 py-2 text-sm">
            <span class="w-24 shrink-0 text-gray-600 dark:text-gray-400">
              {fmtTrackerDate(entry.spentOn, data.today)}
            </span>
            <span class="grow min-w-0 text-gray-900 dark:text-gray-100">
              {channelName(data, entry.channelKey)}
              {entry.note && <span class="text-gray-600 dark:text-gray-400"> · {entry.note}</span>}
            </span>
            <span class="tabular-nums text-gray-900 dark:text-gray-100">
              {spendAmountText(entry)}
            </span>
          </li>
        ))}
      </ul>
      <p class="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
        {[...totals].map(([name, total]) => `${name} ${fmtMoney(total)}`).join(' · ')}
      </p>
    </div>
  );
}
